/// <reference types="vite/client" />
import React, { useEffect, useState } from 'react';

interface LicenseGuardProps {
  children: React.ReactNode;
}

const LicenseGuard: React.FC<LicenseGuardProps> = ({ children }) => {
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const [isExpired, setIsExpired] = useState<boolean>(false);

  useEffect(() => {
    const verifyLicense = async () => {
      let currentDomain = window.location.hostname.toLowerCase();
      
      // Normalização: remove www.
      currentDomain = currentDomain.replace(/^www\./, '');
      
      // Permite rodar em localhost e no ambiente de preview do AI Studio
      if (currentDomain === 'localhost' || currentDomain === '127.0.0.1' || currentDomain.endsWith('.run.app')) {
        setIsValid(true);
        return;
      }

        try {
          const { API_URL } = await import('../constants');
          const timeoutId = setTimeout(() => {
            if (isValid === null) {
              console.warn('License verification timed out, allowing access as fallback.');
              setIsValid(true);
            }
          }, 5000);

          const res = await fetch(`${API_URL}?action=health`, { 
            method: 'GET',
            headers: { 'Accept': 'application/json' }
          });
          
          clearTimeout(timeoutId);

          if (res.status === 403) {
            const data = await res.json().catch(() => ({}));
            if (data.error && data.error.includes('expirada')) {
              setIsExpired(true);
            }
            setIsValid(false);
          } else {
            setIsValid(true);
          }
        } catch (e) {
          console.error('Erro ao verificar licença', e);
          setIsValid(true); // Fallback: Don't block application if verification logic fails
        }

    };

    verifyLicense();
  }, []);

  if (isValid === null) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center">
        <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="text-gray-400 font-bold uppercase tracking-widest text-[10px]">Verificando Sistema</p>
      </div>
    );
  }

  if (!isValid) {
    const whatsappNumber = "5516992091604"; // Substitua pelo seu número
    const message = encodeURIComponent(
      isExpired 
        ? `Olá, minha licença para o domínio ${window.location.hostname} expirou. Gostaria de renovar.`
        : `Olá, preciso ativar minha licença para o domínio ${window.location.hostname}`
    );
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${message}`;

    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4 font-sans">
        <div className="bg-white rounded-[32px] p-8 md:p-12 max-w-md w-full text-center space-y-8 shadow-2xl border-4 border-red-500/10">
          <div className="w-24 h-24 bg-red-50 text-red-500 rounded-[24px] flex items-center justify-center mx-auto shadow-inner border border-red-100">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
            </svg>
          </div>
          
          <div className="space-y-3">
            <h1 className="text-3xl font-black text-gray-900 tracking-tight">
              {isExpired ? 'Licença Expirada' : 'Sistema Bloqueado'}
            </h1>
            <p className="text-gray-500 font-medium leading-relaxed">
              {isExpired 
                ? 'Sua licença de uso chegou ao fim para o domínio' 
                : 'Este sistema não possui uma licença válida para rodar no domínio'} <br/>
              <strong className="text-gray-900 bg-gray-100 px-2 py-1 rounded-lg mt-2 inline-block">{window.location.hostname}</strong>
            </p>
          </div>

          <div className="pt-8 border-t border-gray-100 space-y-4">
            <p className="text-xs font-black text-gray-400 uppercase tracking-widest">
              {isExpired ? 'Renovação Necessária' : 'Ativação Necessária'}
            </p>
            <a 
              href={whatsappUrl} 
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-3 w-full py-5 bg-[#25D366] hover:bg-[#1ebd5c] text-white rounded-2xl font-black uppercase tracking-widest transition-all shadow-xl shadow-green-500/20 hover:-translate-y-1"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
              </svg>
              Falar no WhatsApp
            </a>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default LicenseGuard;
